import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Save, CheckCircle2, AlertTriangle, Inbox, MessageCircle, MessageSquarePlus, BarChart3, Bug, Heart } from 'lucide-react';
import { toast } from 'sonner';
import * as supportApi from '../services/supportApi';
import type { LeanChartSummary } from '../services/supportApi';
import { listEnterprises, listTeams, type Enterprise, type Team } from '../services/organizationApi';

export default function SettingsPage() {
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [enterpriseId, setEnterpriseId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [charts, setCharts] = useState<LeanChartSummary[]>([]);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [bugChartId, setBugChartId] = useState('');
  const [npsChartId, setNpsChartId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settings, ents] = await Promise.all([
          supportApi.getSettings(),
          listEnterprises(),
        ]);
        if (cancelled) return;
        setEnterprises(ents);
        setEnterpriseId(settings.target_enterprise_id || '');
        setTeamId(settings.target_team_id || '');
        setBugChartId(settings.feedback_bug_chart_id || '');
        setNpsChartId(settings.feedback_nps_chart_id || '');
        if (settings.target_enterprise_id) {
          const t = await listTeams(settings.target_enterprise_id);
          if (!cancelled) setTeams(t);
        }
        if (settings.target_team_id) {
          setChartsLoading(true);
          try {
            const c = await supportApi.listChartsByTeam(settings.target_team_id);
            if (!cancelled) setCharts(c);
          } finally {
            if (!cancelled) setChartsLoading(false);
          }
        }
      } catch (err) {
        console.error('[settings] load failed', err);
        toast.error('Impossible de charger la configuration.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reloadChartsForTeam = async (newTeamId: string) => {
    if (!newTeamId) {
      setCharts([]);
      return;
    }
    setChartsLoading(true);
    try {
      const c = await supportApi.listChartsByTeam(newTeamId);
      setCharts(c);
    } catch (err) {
      console.error('[settings] listChartsByTeam failed', err);
      toast.error('Impossible de charger les graphes de la team cible.');
      setCharts([]);
    } finally {
      setChartsLoading(false);
    }
  };

  const handleEnterpriseChange = async (id: string) => {
    setEnterpriseId(id);
    setTeamId('');
    setBugChartId('');
    setNpsChartId('');
    setCharts([]);
    setDirty(true);
    if (id) {
      try {
        const t = await listTeams(id);
        setTeams(t);
      } catch (err) {
        console.error('[settings] listTeams failed', err);
        toast.error('Impossible de charger les équipes.');
        setTeams([]);
      }
    } else {
      setTeams([]);
    }
  };

  const handleTeamChange = async (id: string) => {
    setTeamId(id);
    setBugChartId('');
    setNpsChartId('');
    setDirty(true);
    await reloadChartsForTeam(id);
  };

  const handleBugChartChange = (id: string) => {
    setBugChartId(id);
    setDirty(true);
  };

  const handleNpsChartChange = (id: string) => {
    setNpsChartId(id);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!enterpriseId || !teamId) return;
    setSaving(true);
    try {
      const bugChartIdToSend = bugChartId ? parseInt(bugChartId, 10) : null;
      const npsChartIdToSend = npsChartId ? parseInt(npsChartId, 10) : null;
      await supportApi.saveSettings(enterpriseId, teamId, bugChartIdToSend, npsChartIdToSend);
      toast.success('Configuration sauvegardée.');
      setDirty(false);
    } catch (err) {
      console.error('[settings] save failed', err);
      toast.error('Échec de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const selectedEnterprise = enterprises.find((e) => e.id === enterpriseId);
  const selectedTeam = teams.find((t) => t.id === teamId);
  const selectedChart = charts.find((c) => String(c.id) === bugChartId);
  const selectedNpsChart = charts.find((c) => String(c.id) === npsChartId);
  const isConfigured = !!enterpriseId && !!teamId;
  const canSave = isConfigured && dirty && !saving;

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <SettingsIcon className="w-6 h-6 text-indigo-600" />
          Settings
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Configuration globale de la plateforme EasyObeya.
        </p>
      </div>

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <header className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Inbox className="w-4 h-4 text-indigo-600" />
            Routage des problèmes externes
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Ces deux variables (<code className="px-1 bg-gray-100 rounded">target_enterprise_id</code>
            {' / '}
            <code className="px-1 bg-gray-100 rounded">target_team_id</code>) déterminent où sont
            créés les problèmes EasyKaizen générés depuis :
          </p>
          <ul className="mt-2 text-xs text-gray-600 space-y-1">
            <li className="flex items-center gap-2">
              <MessageCircle className="w-3.5 h-3.5 text-blue-500" />
              Les conversations Support utilisateur (auto-création de problème lié à la conversation)
            </li>
            <li className="flex items-center gap-2">
              <MessageSquarePlus className="w-3.5 h-3.5 text-blue-500" />
              Les retours utilisateur du footer « Donnez votre avis » (bug / amélioration)
            </li>
          </ul>
        </header>

        <div className="px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent" />
            </div>
          ) : (
            <>
              {!isConfigured && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Configuration incomplète</p>
                    <p className="text-xs mt-0.5">
                      Tant que cette config est vide, les problèmes ne seront pas créés
                      automatiquement (les conversations et emails de notification continuent
                      de fonctionner).
                    </p>
                  </div>
                </div>
              )}

              {isConfigured && !dirty && (
                <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Configuration active</p>
                    <p className="text-xs mt-0.5">
                      Les problèmes sont créés dans{' '}
                      <strong>{selectedEnterprise?.name ?? enterpriseId}</strong>
                      {' / '}
                      <strong>{selectedTeam?.name ?? teamId}</strong>.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enterprise cible
                  </label>
                  <select
                    value={enterpriseId}
                    onChange={(e) => handleEnterpriseChange(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">— Sélectionner —</option>
                    {enterprises.map((e) => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                  {enterpriseId && (
                    <p className="mt-1 text-xs text-gray-400 font-mono truncate">{enterpriseId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team cible
                  </label>
                  <select
                    value={teamId}
                    onChange={(e) => handleTeamChange(e.target.value)}
                    disabled={!enterpriseId || teams.length === 0}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">— Sélectionner —</option>
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  {teamId && (
                    <p className="mt-1 text-xs text-gray-400 font-mono truncate">{teamId}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Section: compteur de bugs EasyCharts */}
      <section className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <header className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-indigo-600" />
            Compteur de bugs (EasyCharts)
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            À chaque soumission d'un feedback de type{' '}
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[11px]">
              <Bug className="w-3 h-3" /> Bug
            </span>{' '}
            via le footer « Donnez votre avis », la valeur du jour du graphe sélectionné est incrémentée de 1.
            Les améliorations ne sont pas comptabilisées.
          </p>
        </header>

        <div className="px-6 py-5 space-y-4">
          {!isConfigured ? (
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-500">
              Configurez d'abord l'enterprise et la team cible ci-dessus pour pouvoir choisir un graphe.
            </div>
          ) : chartsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-500 border-t-transparent" />
              Chargement des graphes…
            </div>
          ) : charts.length === 0 ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              Aucun graphe trouvé pour cette team. Créez d'abord un graphe (par ex. « Bugs reportés ») dans EasyObeya côté {selectedTeam?.name ?? 'team cible'}.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Graphe cible
                </label>
                <select
                  value={bugChartId}
                  onChange={(e) => handleBugChartChange(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">— Aucun (compteur désactivé) —</option>
                  {charts.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}{c.periodicity ? ` (${c.periodicity})` : ''}
                    </option>
                  ))}
                </select>
                {bugChartId && selectedChart && (
                  <p className="mt-1 text-xs text-gray-400 font-mono">
                    id={selectedChart.id} · uuid={selectedChart.uuid}
                  </p>
                )}
              </div>

              {!bugChartId && (
                <p className="text-xs text-gray-500">
                  Compteur désactivé : aucun graphe ne sera incrémenté lors des soumissions de bugs.
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {/* Section: graphe NPS EasyCharts */}
      <section className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <header className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Heart className="w-4 h-4 text-pink-600" />
            Graphe NPS (EasyCharts)
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            À chaque soumission d'un feedback de type{' '}
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-pink-50 text-pink-700 text-[11px]">
              <Heart className="w-3 h-3" /> NPS
            </span>{' '}
            via le footer « Donnez votre avis », la note (0–10) est classée (Détracteur / Neutre / Promoteur)
            et le compteur correspondant du graphe sélectionné est incrémenté de 1 pour la date du jour.
            Le graphe doit être de type NPS.
          </p>
        </header>

        <div className="px-6 py-5 space-y-4">
          {!isConfigured ? (
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-500">
              Configurez d'abord l'enterprise et la team cible ci-dessus pour pouvoir choisir un graphe.
            </div>
          ) : chartsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-pink-500 border-t-transparent" />
              Chargement des graphes…
            </div>
          ) : charts.length === 0 ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              Aucun graphe trouvé pour cette team. Créez d'abord un graphe NPS (carte « Indicateurs spéciaux ») dans EasyObeya côté {selectedTeam?.name ?? 'team cible'}.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Graphe NPS cible
                </label>
                <select
                  value={npsChartId}
                  onChange={(e) => handleNpsChartChange(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                >
                  <option value="">— Aucun (collecte NPS désactivée) —</option>
                  {charts.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}{c.periodicity ? ` (${c.periodicity})` : ''}
                    </option>
                  ))}
                </select>
                {npsChartId && selectedNpsChart && (
                  <p className="mt-1 text-xs text-gray-400 font-mono">
                    id={selectedNpsChart.id} · uuid={selectedNpsChart.uuid}
                  </p>
                )}
              </div>

              {!npsChartId && (
                <p className="text-xs text-gray-500">
                  Collecte désactivée : les notes NPS ne seront pas reportées sur un graphe (les conversations support et les insights EasyClient continuent d'être créés).
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {/* Save bar globale aux 2 sections */}
      <div className="mt-6 flex items-center justify-end gap-3">
        {dirty && (
          <span className="text-xs text-amber-600">Modifications non sauvegardées</span>
        )}
        <button
          onClick={handleSave}
          disabled={!canSave}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </button>
      </div>
    </div>
  );
}
